import CampSettingsEditor from '@/pages/backend/components/CampSettingsEditor';
import { getCampInfoById, listCampNodeOption } from '@/services/ConsoleRequest';
import { TConditionForSuccess, TFinishRule } from '@/types/camp/campIndex/indexTypes';
import { TOption } from '@/types/interface';
import { useRequest, useUpdateEffect } from 'ahooks';
import {
  Button,
  Checkbox,
  Flex,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Spin,
  Typography,
} from 'antd';
import cx from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'umi';
import { useImmer } from 'use-immer';
import styles from './index.less';
import { updateFinishRule } from '@/services/CardCampRequest';
import { UncommittedProps } from '@/pages/backend/cardCampSettings';
import { isEmpty } from 'lodash';
import { QuestionCircleOutlined } from '@ant-design/icons';

const SuccessFormItem = ({
  value,
  onChange,
  options,
  optionsLoading,
}: {
  value?: TConditionForSuccess;
  onChange?: (value?: TConditionForSuccess) => void;
  options: TOption[];
  optionsLoading: any;
}) => {
  // @ts-ignore
  const [data, updateData] = useImmer<TConditionForSuccess>(value || {});

  useUpdateEffect(() => {
    if (data !== value) {
      if (
        (data.selectedEarnCredits && data.earnCredits) ||
        (data.selectedFinishNodes && !isEmpty(data.finishNodes))
      ) {
        onChange?.(data);
      } else {
        onChange?.(undefined);
      }
    }
  }, [data]);

  useEffect(() => {
    if (value) {
      updateData(value);
    }
  }, [value]);

  return (
    <div className={cx('max-w-[526px]', 'p-[10px]', 'bg-[#F8F8FA]')}>
      <Flex gap={8} align={'center'}>
        <Checkbox
          checked={data?.selectedFinishNodes}
          onChange={(e) => {
            updateData((draft) => {
              draft.selectedFinishNodes = e.target.checked;
            });
          }}
        />
        <span>完成节点：</span>
        <Form.Item
          noStyle
          validateStatus={
            data.selectedFinishNodes && isEmpty(data.finishNodes) ? 'error' : undefined
          }
        >
          <Spin spinning={optionsLoading}>
            <Select
              disabled={!data.selectedFinishNodes}
              mode="multiple"
              allowClear
              maxTagCount={'responsive'}
              value={data.finishNodes}
              style={{ width: '400px' }}
              options={options.map((item) => ({ value: item.id, label: item.name }))}
              onChange={(value) => {
                updateData((draft) => {
                  draft.finishNodes = value;
                });
              }}
            />
          </Spin>
        </Form.Item>
      </Flex>
      <Flex gap={8} align={'center'} className={'mt-[10px]'}>
        <Checkbox
          checked={data.selectedEarnCredits}
          onChange={(e) => {
            updateData((draft) => {
              draft.selectedEarnCredits = e.target.checked;
            });
          }}
        />
        <span>获得学分：</span>
        <Form.Item
          noStyle
          validateStatus={data.selectedEarnCredits && !data.earnCredits ? 'error' : undefined}
        >
          <InputNumber
            disabled={!data.selectedEarnCredits}
            min={0}
            value={data.earnCredits}
            addonBefore="≥"
            addonAfter="学分"
            className={cx('max-w-[400px]')}
            onChange={(value) => {
              updateData((draft) => {
                draft.earnCredits = Number(value);
              });
            }}
          />
        </Form.Item>
      </Flex>
      <p className={cx('mt-[10px]', 'text-[#8D8D8D]')}>启用的多个条件之间为且关系</p>
    </div>
  );
};

const FinishRuleSettings = ({ hasUncommitted, setHasUncommitted }: UncommittedProps) => {
  const { id: campId } = useParams<{ id: string }>();
  const [campNodeOptions, setCampNodeOptions] = useState<TOption[]>([]);
  const [form] = Form.useForm();
  const [firstInit, setFirstInit] = useState(true);
  const [data, setData] = useState<TFinishRule>();

  const { loading } = useRequest(
    () => {
      return getCampInfoById(campId);
    },
    {
      onSuccess: (res) => {
        setData(res.data.finishRule);
        form.setFieldsValue({
          ...res.data.finishRule,
        });
      },
    },
  );

  const promptAfterSuccess = useMemo(() => {
    if (data?.promptAfterSuccess && typeof data?.promptAfterSuccess === 'string') {
      try {
        return JSON.parse(data?.promptAfterSuccess);
      } catch (e) {
        return '';
      }
    }
    return '';
  }, [data?.promptAfterSuccess]);

  const { loading: campNodeLoading } = useRequest(
    () => {
      return listCampNodeOption(campId);
    },
    {
      onSuccess: (res) => {
        setCampNodeOptions(res.data);
      },
    },
  );

  const { run: updateFinishRuleRun } = useRequest(
    (campId: string, data: TFinishRule) => {
      if (data) {
        return updateFinishRule(campId, data);
      } else {
        return Promise.reject('finishRule is null');
      }
    },
    {
      manual: true,
      onSuccess: () => {
        message.success('保存成功');
        setHasUncommitted(false);
      },
      onError: () => {
        message.error('保存失败');
      },
    },
  );

  const [modal, contextHolder] = Modal.useModal();

  return (
    <div>
      {contextHolder}
      <Spin spinning={loading}>
        <Form
          className={styles.FormWrapper}
          form={form}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 18 }}
          onValuesChange={(changedValues, values) => {
            if (firstInit) {
              setFirstInit(false);
              return;
            }
            console.log(`changed`, changedValues, values);
            setHasUncommitted(true);
          }}
        >
          <Form.Item
            label="打卡成功条件"
            name="conditionForSuccess"
            required
            validateStatus={'success'}
            rules={[
              { required: true, message: '请设置打卡成功条件' },
              {
                validator: (_, value: TConditionForSuccess) => {
                  if (!value) {
                    return Promise.resolve();
                  }
                  if (value.selectedFinishNodes && isEmpty(value.finishNodes)) {
                    return Promise.reject('请选择完成节点');
                  }
                  if (value.selectedEarnCredits && !value.earnCredits) {
                    return Promise.reject('请输入获得学分');
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <SuccessFormItem options={campNodeOptions} optionsLoading={campNodeLoading} />
          </Form.Item>
          <Form.Item label="优秀学员学分" className={cx(styles.excellentCreditLimit)}>
            <Form.Item noStyle name="excellentCreditLimit">
              <InputNumber min={0} addonBefore="≥" />
            </Form.Item>
            <div className={cx('h-[54px]', 'flex-center')}>
              <Typography.Text
                className={cx('text-[#C0C0C0]', 'text-sm', 'font-normal')}
                type="secondary"
              >
                用户打卡成功时如果总学分大于等于所设置的分值则生成优秀学员证书。为空时不设优秀学员，不生成优秀学员证书。
              </Typography.Text>
            </div>
          </Form.Item>
          <Form.Item label="下载报告按钮文案" className={cx(styles.excellentCreditLimit)}>
            <Form.Item noStyle name="downloadReportBtnText">
              <Input showCount maxLength={50} />
            </Form.Item>
            <div className={cx('h-[54px]', 'flex-center')}>
              <Typography.Text
                className={cx('text-[#C0C0C0]', 'text-sm', 'font-normal')}
                type="secondary"
              >
                可以自定义学习报告导出图片的引导文案，未填写时使用默认文案“立即生成个人专属荣誉海报”
              </Typography.Text>
            </div>
          </Form.Item>
          <Form.Item label="打卡成功后提示" name="promptAfterSuccess" valuePropName="content">
            <CampSettingsEditor style={{ height: '400px' }} />
          </Form.Item>
        </Form>
      </Spin>
      <div className="config-footer">
        <Button
          type={'primary'}
          onClick={() => {
            form.validateFields().then((values) => {
              modal.confirm({
                title: '确认保存吗？',
                okText: '确认',
                cancelText: '取消',
                icon: <QuestionCircleOutlined />,
                onOk() {
                  updateFinishRuleRun(campId, {
                    ...values,
                    promptAfterSuccess:
                      typeof values.promptAfterSuccess === 'string'
                        ? values.promptAfterSuccess
                        : JSON.stringify(values.promptAfterSuccess),
                  });
                },
              });
            });
          }}
        >
          保存更改
        </Button>
      </div>
    </div>
  );
};

export default FinishRuleSettings;
